'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authDebug, authDebugError, maskEmail } from '@/lib/authDebug';
import { createClient } from '../lib/supabase/client';

type AuthContextValue = {
  user: any;
  session: any;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: () => boolean;
  refreshSession: () => Promise<any>;
  signUp: (
    email: string,
    password: string,
    metadata?: {
      fullName?: string;
      avatarUrl?: string;
      businessName?: string;
      phone?: string;
      role?: string;
    }
  ) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  resendVerificationEmail: (email?: string) => Promise<void>;
  getUserProfile: () => Promise<any>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Stable supabase client — created once, never recreated on re-render
  const supabase = useMemo(() => createClient(), []);

  // Stable ref to supabase so callbacks have zero deps on it
  const supabaseRef = useRef(supabase);
  useEffect(() => { supabaseRef.current = supabase; }, [supabase]);

  // ─── Guards ────────────────────────────────────────────────────────────────
  // Prevent React Strict Mode double-mount from initializing session twice
  const sessionInitializedRef = useRef(false);

  // Prevent concurrent signIn calls from firing multiple signInWithPassword requests
  const signInInFlightRef = useRef(false);

  // Deduplicate refreshSession calls — N concurrent callers share ONE promise
  const refreshInFlightRef = useRef<Promise<any> | null>(null);

  // Rate-limit cooldown for refreshSession — don't retry until this timestamp
  const refreshRateLimitUntilRef = useRef(0);

  // Prevent concurrent resendVerificationEmail calls
  const resendInFlightRef = useRef(false);
  // Timestamp (ms) until which resend is rate-limited at the context level
  const resendRateLimitUntilRef = useRef(0);

  // Flag to block ALL session rehydration after signOut is called
  const isSigningOutRef = useRef(false);

  // Keep a stable ref to current user so callbacks don't need user in deps
  const userRef = useRef<any>(null);
  const sessionRef = useRef<any>(null);
  useEffect(() => {
    userRef.current = user;
    sessionRef.current = session;
  }, [user, session]);

  const isAdmin = useCallback(() =>
    user?.user_metadata?.role === 'admin' ||
    user?.app_metadata?.role === 'admin' ||
    user?.email === 'admin@zipadbook.com',
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [user]);

  // Stable ref to isAdmin so consumers can call it without adding it to deps
  const isAdminRef = useRef(isAdmin);
  useEffect(() => { isAdminRef.current = isAdmin; }, [isAdmin]);

  const isUserEmailVerified = (candidate: any) => {
    return Boolean(candidate?.email_confirmed_at || candidate?.confirmed_at);
  };

  const isEmailNotConfirmedError = (error: any) => {
    const message = String(error?.message || '').toLowerCase();
    const code = String(error?.code || '').toLowerCase();
    return (
      code === 'email_not_confirmed' || message.includes('email not confirmed') ||
      message.includes('email_not_confirmed') ||
      message.includes('email not verified')
    );
  };

  const isRateLimitError = (error: any) => {
    const message = String(error?.message || '').toLowerCase();
    const code = String(error?.code || '').toLowerCase();
    return (
      (error as any)?.status === 429 ||
      code === 'over_request_rate_limit' ||
      code === 'too_many_requests' ||
      code === 'over_email_send_rate_limit'|| message.includes('rate limit') ||
      message.includes('too many requests') ||
      message.includes('request rate limit') ||
      message.includes('rate limit reached')
    );
  };

  const getAuthRedirectTo = useCallback(() => {
    const callbackPath = '/auth/callback';
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return `http://localhost:3000${callbackPath}`;
    }
    const productionUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://zipadbook.com';
    return `${productionUrl}${callbackPath}`;
  }, []);

  // ─── refreshSession (with mutex deduplication) ─────────────────────────────
  const refreshSession = useCallback(async () => {
    // Block refresh if signOut is in progress
    if (isSigningOutRef.current) {
      authDebug('refreshSession:blocked-signing-out');
      return null;
    }

    // Fast path: if we already have a valid user, return immediately — no network call.
    if (userRef.current) {
      authDebug('refreshSession:returning-cached-user', {
        userId: userRef.current?.id,
        source: 'cache',
      });
      return userRef.current;
    }

    // Rate-limit cooldown: skip network call if we were recently rate-limited
    if (Date.now() < refreshRateLimitUntilRef.current) {
      authDebug('refreshSession:skipped-rate-limit-cooldown');
      return null;
    }

    // Mutex: if a refresh is already in-flight, return the same promise.
    if (refreshInFlightRef.current) {
      authDebug('refreshSession:deduped-joining-in-flight-request');
      return refreshInFlightRef.current;
    }

    authDebug('refreshSession:starting-new-request', { t: new Date().toISOString() });

    const promise = (async () => {
      try {
        const { data, error } = await supabaseRef.current.auth.getSession();

        if (error) {
          const isRateLimit =
            (error as any)?.status === 429 ||
            (error?.message || '').toLowerCase().includes('rate limit') ||
            (error?.message || '').toLowerCase().includes('request rate limit') ||
            (error as any)?.code === 'over_request_rate_limit' ||
            (error as any)?.code === 'too_many_requests';

          if (isRateLimit) {
            authDebug('refreshSession:rate-limited-keeping-current-state');
            // Back off for 5 minutes before allowing another refresh attempt
            refreshRateLimitUntilRef.current = Date.now() + 5 * 60 * 1000;
            return userRef.current;
          }

          authDebugError('refreshSession:getSession-error', error);
          if (userRef.current) return userRef.current;
          setSession(null);
          setUser(null);
          return null;
        }

        if (data.session) {
          const sessionUser = data.session.user ?? null;
          if (sessionUser && !isUserEmailVerified(sessionUser)) {
            authDebug('refreshSession:drop-unverified-session');
            await supabaseRef.current.auth.signOut();
            setSession(null);
            setUser(null);
            return null;
          }
          setSession(data.session);
          setUser(sessionUser);
          return sessionUser;
        }

        setSession(null);
        setUser(null);
        return null;
      } finally {
        refreshInFlightRef.current = null;
      }
    })();

    refreshInFlightRef.current = promise;
    return promise;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase]);

  useEffect(() => {
    // ─── Patch console.error FIRST — before onAuthStateChange fires ───────────
    // Supabase's internal auth initialization can fire rate-limit errors via
    // console.error synchronously during onAuthStateChange setup. Patching here
    // (in addition to the module-level patch in client.tsx) ensures coverage
    // even if the module-level patch was not yet applied.
    const _originalConsoleError = console.error;
    const SUPPRESSED_PATTERNS = [
      'failed to fetch', 'networkerror', 'network request failed',
      'request rate limit', 'rate limit reached', 'over_request_rate_limit',
      'rate limit', 'too many requests', 'autoapierror',
    ];
    console.error = (...args: any[]) => {
      const msg = args.map(a => {
        if (a && typeof a === 'object') {
          return String(a?.message || a?.name || a?.code || JSON.stringify(a) || '');
        }
        return String(a || '');
      }).join(' ').toLowerCase();
      if (SUPPRESSED_PATTERNS.some(p => msg.includes(p))) {
        authDebug('global:suppressed-console-error', { msg });
        return;
      }
      _originalConsoleError.apply(console, args);
    };

    // ─── Single source of truth: onAuthStateChange ────────────────────────────
    // ALL state updates flow through here. signIn/signUp do NOT call setUser/setSession
    // directly — this prevents the double-update that was causing duplicate API calls.
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
      try {
        authDebug('onAuthStateChange:event', {
          event: _event,
          hasSession: Boolean(newSession),
          userId: newSession?.user?.id ?? null,
          t: new Date().toISOString(),
        });

        // ── Always handle SIGNED_OUT immediately — never skip or deduplicate ────
        if (_event === 'SIGNED_OUT') {
          authDebug('onAuthStateChange:signed-out-clearing-state');
          isSigningOutRef.current = false;
          refreshInFlightRef.current = null;
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }

        // ── Block any session restoration while signOut is in progress ──────────
        if (isSigningOutRef.current) {
          authDebug('onAuthStateChange:blocked-signing-out', { event: _event });
          setLoading(false);
          return;
        }

        // ── Deduplication: skip if session/user hasn't actually changed ──────────
        // This prevents redundant state updates when Supabase fires multiple events
        // for the same session (e.g., INITIAL_SESSION + SIGNED_IN on login).
        const newUserId = newSession?.user?.id ?? null;
        const currentUserId = userRef.current?.id ?? null;
        const currentSessionToken = sessionRef.current?.access_token ?? null;
        const newSessionToken = newSession?.access_token ?? null;

        if (
          _event !== 'TOKEN_REFRESHED' &&
          newUserId === currentUserId &&
          newSessionToken === currentSessionToken &&
          currentUserId !== null
        ) {
          authDebug('onAuthStateChange:skipped-no-change', { event: _event, userId: newUserId });
          setLoading(false);
          return;
        }

        // Handle TOKEN_REFRESHED failure
        if (_event === 'TOKEN_REFRESHED' && !newSession) {
          authDebug('onAuthStateChange:token-refresh-failed-clearing-session');
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }

        const eventUser = newSession?.user ?? null;
        if (eventUser && !isUserEmailVerified(eventUser)) {
          authDebug('onAuthStateChange:drop-unverified-user', {
            email: maskEmail(eventUser.email || ''),
            userId: eventUser.id,
            event: _event,
          });
          await supabase.auth.signOut();
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }

        setSession(newSession);
        setUser(eventUser);
        setLoading(false);
      } catch (err: any) {
        // Gracefully handle network errors (e.g., "Failed to fetch") from Supabase internals
        const msg = String(err?.message || '').toLowerCase();
        if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('network request failed')) {
          authDebug('onAuthStateChange:network-error-ignored', { event: _event, message: err?.message });
          setLoading(false);
          return;
        }
        // Re-throw non-network errors
        throw err;
      }
    });

    // ─── Initialize session on mount ──────────────────────────────────────────
    const initializeSession = async () => {
      if (sessionInitializedRef.current) {
        authDebug('initializeSession:skipped-already-initialized');
        return;
      }
      sessionInitializedRef.current = true;

      // onAuthStateChange fires INITIAL_SESSION synchronously/immediately and
      // provides the session — we do NOT call getSession() here to avoid a
      // redundant API round-trip that triggers Supabase rate limiting.
      // setLoading(false) is handled inside the onAuthStateChange handler when
      // the INITIAL_SESSION event fires.
      authDebug('initializeSession:delegated-to-onAuthStateChange', { t: new Date().toISOString() });

      // Safety fallback: if onAuthStateChange hasn't resolved loading within
      // 3 seconds (e.g., network issue), clear the loading state.
      const fallbackTimer = setTimeout(() => {
        if (loading) {
          authDebug('initializeSession:fallback-timeout-clearing-loading');
          setLoading(false);
        }
      }, 3000);

      return () => clearTimeout(fallbackTimer);
    };

    initializeSession();

    // ─── Global unhandled rejection handler for Supabase network errors ───────
    // Supabase's internal _refreshAccessToken / _callRefreshToken can throw
    // "Failed to fetch" as an unhandled promise rejection when the network is
    // unavailable. We catch and suppress these to prevent console noise.
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const msg = String(event?.reason?.message || event?.reason || '').toLowerCase();
      if (
        msg.includes('failed to fetch') ||
        msg.includes('networkerror') ||
        msg.includes('network request failed') ||
        msg.includes('rate limit') ||
        msg.includes('request rate limit') ||
        msg.includes('rate limit reached') ||
        msg.includes('over_request_rate_limit') ||
        msg.includes('too many requests')
      ) {
        event.preventDefault();
        authDebug('global:suppressed-fetch-rejection', { message: msg });
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('unhandledrejection', handleUnhandledRejection);
    }

    return () => {
      subscription.unsubscribe();
      console.error = _originalConsoleError;
      if (typeof window !== 'undefined') {
        window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      }
    };
  }, [supabase]);

  // Email/Password Sign Up
  const signUp = useCallback(async (
    email: string,
    password: string,
    metadata: {
      fullName?: string;
      avatarUrl?: string;
      businessName?: string;
      phone?: string;
      role?: string;
    } = {}
  ) => {
    const redirectTo = getAuthRedirectTo();
    authDebug('signUp:start', {
      email: maskEmail(email),
      emailRedirectTo: redirectTo,
      t: new Date().toISOString(),
    });
    const { data, error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          business_name: metadata?.businessName || '',
          phone: metadata?.phone || '',
          role: metadata?.role || 'advertiser',
          email_verification_status: 'pending'
        },
        emailRedirectTo: redirectTo
      }
    });
    if (error) {
      authDebugError('signUp:supabase-error', error, { email: maskEmail(email) });
      if (isRateLimitError(error)) {
        throw new Error('Too many sign-up attempts. Please wait a few minutes and try again.');
      }
      // Wrap AuthApiError so parseAuthError receives a proper Error instance
      throw new Error(error.message || 'Could not create your account. Please try again.');
    }

    // Never auto-login on signup; access stays blocked until verified link is completed.
    // Do NOT call setSession/setUser here — let onAuthStateChange handle state.
    authDebug('signUp:success', {
      email: maskEmail(email),
      hasSession: Boolean(data?.session),
    });

    return data;
  }, [supabase, getAuthRedirectTo]);

  // ─── Email/Password Sign In ────────────────────────────────────────────────
  // CRITICAL: Does NOT call setSession/setUser directly.
  // State is updated exclusively by onAuthStateChange (SIGNED_IN event).
  // This prevents the double state-update that was causing duplicate API calls.
  const signIn = useCallback(async (email: string, password: string) => {
    // Strict in-flight guard: block concurrent signIn calls at the context level
    if (signInInFlightRef.current) {
      authDebug('signIn:blocked-already-in-flight', { email: maskEmail(email) });
      throw new Error('Login already in progress. Please wait.');
    }
    signInInFlightRef.current = true;

    authDebug('signIn:start', {
      email: maskEmail(email),
      t: new Date().toISOString(),
    });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        if (isRateLimitError(error)) {
          authDebug('signIn:rate-limited', {
            email: maskEmail(email),
            code: (error as any)?.code,
            status: (error as any)?.status,
          });
          throw new Error('Too many login attempts. Please wait a few minutes before trying again.');
        }
        if (isEmailNotConfirmedError(error)) {
          authDebug('signIn:blocked-email-not-confirmed', {
            email: maskEmail(email),
            code: (error as { code?: string }).code,
          });
          throw new Error('Please verify your email before accessing your dashboard.');
        }
        authDebugError('signIn:supabase-error', error, { email: maskEmail(email) });
        throw error;
      }

      const signedInUser = data.user ?? data.session?.user ?? null;
      if (!isUserEmailVerified(signedInUser)) {
        authDebug('signIn:blocked-user-not-verified-after-password', {
          email: maskEmail(email),
          userId: signedInUser?.id,
        });
        await supabase.auth.signOut();
        // Do NOT call setSession/setUser — onAuthStateChange SIGNED_OUT will handle it
        throw new Error('Please verify your email before accessing your dashboard.');
      }

      // ✅ Do NOT call setSession/setUser here.
      // onAuthStateChange will fire SIGNED_IN and update state exactly once.
      authDebug('signIn:success', {
        email: maskEmail(email),
        userId: signedInUser?.id,
        t: new Date().toISOString(),
        note: 'state-update-delegated-to-onAuthStateChange',
      });
      return data;
    } finally {
      signInInFlightRef.current = false;
    }
  }, [supabase]);

  // Sign Out
  const signOut = useCallback(async () => {
    authDebug('signOut:start', { t: new Date().toISOString() });

    // 1. Set signing-out flag FIRST to block any concurrent refresh/rehydration
    isSigningOutRef.current = true;

    // 2. Cancel any in-flight refresh promise immediately
    refreshInFlightRef.current = null;
    signInInFlightRef.current = false;

    // 3. Clear React state immediately — UI updates without waiting for listener
    setSession(null);
    setUser(null);
    userRef.current = null;
    sessionRef.current = null;
    setLoading(false);

    // 4. Call supabase.auth.signOut() FIRST — needs the stored token to revoke server-side session
    //    This triggers SIGNED_OUT in onAuthStateChange which resets isSigningOutRef
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        authDebugError('signOut:supabase-error', error);
        isSigningOutRef.current = false;
      }
    } catch (err) {
      authDebugError('signOut:unexpected-error', err);
      isSigningOutRef.current = false;
    }

    // 5. Clear all Supabase/GoTrue localStorage keys AFTER signOut completes
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sb-') || key.startsWith('supabase') || key.includes('gotrue'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch {
      // localStorage may not be available in all environments
    }

    authDebug('signOut:complete');
  }, [supabase]);

  // Get Current User
  const getCurrentUser = useCallback(async () => {
    authDebug('getCurrentUser:start', { t: new Date().toISOString() });
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  }, [supabase]);

  // Check if Email is Verified
  const isEmailVerified = useCallback(() => {
    return isUserEmailVerified(user);
  }, [user]);

  const resendVerificationEmail = useCallback(async (email?: string) => {
    // Pre-call in-flight guard: block concurrent resend calls
    if (resendInFlightRef.current) {
      authDebug('resendVerificationEmail:blocked-already-in-flight');
      throw new Error('A verification email is already being sent. Please wait.');
    }
    // Pre-call rate-limit guard: block if we know we are rate-limited
    if (resendRateLimitUntilRef.current > Date.now()) {
      const secsLeft = Math.ceil((resendRateLimitUntilRef.current - Date.now()) / 1000);
      authDebug('resendVerificationEmail:blocked-rate-limited', { secsLeft });
      throw new Error(`Too many resend attempts. Please wait ${secsLeft} seconds before trying again.`);
    }

    resendInFlightRef.current = true;
    try {
      const targetEmail = (email || user?.email || '').trim();
      if (!targetEmail) throw new Error('No email found to resend verification.');

      const redirectTo = getAuthRedirectTo();
      authDebug('resendVerificationEmail:start', {
        email: maskEmail(targetEmail),
        emailRedirectTo: redirectTo,
        t: new Date().toISOString(),
      });
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: targetEmail.toLowerCase(),
        options: {
          emailRedirectTo: redirectTo
        }
      });
      if (error) {
        authDebugError('resendVerificationEmail:supabase-error', error, {
          email: maskEmail(targetEmail),
        });
        if (isRateLimitError(error)) {
          // Record rate-limit expiry so subsequent calls are blocked without hitting the API
          resendRateLimitUntilRef.current = Date.now() + 5 * 60 * 1000;
          throw new Error('Too many resend attempts. Please wait a few minutes before requesting another verification email.');
        }
        throw error;
      }
      authDebug('resendVerificationEmail:success', {
        email: maskEmail(targetEmail),
        via: 'supabase-auth-resend',
      });
    } finally {
      resendInFlightRef.current = false;
    }
  }, [supabase, user, getAuthRedirectTo]);

  // Get User Profile from Database
  const getUserProfile = useCallback(async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  }, [supabase, user]);

  const value = useMemo(
    () => ({
      user,
      session,
      loading,
      isAuthenticated: Boolean(user || session),
      isAdmin,
      refreshSession,
      signUp,
      signIn,
      signOut,
      getCurrentUser,
      isEmailVerified,
      resendVerificationEmail,
      getUserProfile
    }),
    [user, session, loading, isAdmin, refreshSession, signUp, signIn, signOut, getCurrentUser, isEmailVerified, resendVerificationEmail, getUserProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
